<template>
    <div class="search-results-map">
        <div class="results-list d-none d-lg-block" :class="showResultsList && 'showResultsList'" ref="resultsList">
            <button class="toggle-btn no-style-btn" @click="handleToggleList" tabindex="0" aria-label="Resultatlista" :aria-expanded="showResultsList">
                <i class="fas fa-chevron-right fa-2x"></i>
            </button>
            <div v-if="isSearching" class="border-bottom p-3">
                Hämtar kulturarv...
                <i class="fa fa-spin fa-spinner" />
            </div>
            <div v-else class="wrapper">
                <div v-if="isSearch && searchResults.locations" class="border-bottom p-3 h-auto">
                  <div v-if="searchQuery !== ''">{{ _.get(searchResults.locations, 'count') }} platser hittades för {{ searchQuery }}</div>
                  <div v-else>{{ _.get(searchResults.locations, 'count') }} platser hittades</div>
                </div>
                <div v-if="isSearch && numWalks > 0" class="border-bottom p-3 h-auto">
                  <div v-if="searchQuery !== ''">{{ numWalks }} Strövtåg hittades för {{ searchQuery }}</div>
                  <div v-else>{{ numWalks }} Strövtåg hittades</div>
                </div>
                <transition name="no-places">
                    <div v-if="!isSearch && locationsList && locationsList.length === 0" class="border-bottom p-3">
                        <div>Du visar inga platser i kartan just nu.</div>
                        <a href="#" @click.prevent="handleVastmanlandClick">Visa hela Västmanland</a>
                    </div>
                </transition>
                <transition-group name="list" tag="div" id="read-this">
                    <button
                            tabindex="0"
                            :key="location.id"
                            v-for="location in locationsList"
                            class="result-item border-bottom p-3 no-style-btn"
                            @click="handleFocusLocation(location)"
                            @mouseover="handleHoverLocation(location)"
                            @mouseout="handleHoverLocation(location, true)">
                        <h5 class="mb-0">{{ location.name }}</h5>
                        <span><i class="far fa-file-alt" /> {{ location.num_related_articles }} {{ location.num_related_articles === 1 ? 'artikel' : 'artiklar' }}</span>
                        <span v-if="location.distance"> | <i class="fas fa-walking" /> {{ getDistanceString(location.distance) }}</span>
                    </button>
                </transition-group>
                <transition-group name="list" tag="div">
                    <button
                            tabindex="0"
                            :key="walk.id"
                            v-for="walk in walksList"
                            class="result-item border-bottom p-3 no-style-btn"
                            @click="handleFocusLocation(walk)">
                        <h5 class="mb-0">{{ walk.title }}</h5>
                        <span><i class="fas fa-route" /> Strövtåg</span>
                    </button>
                </transition-group>
            </div>
        </div>
        <div class="map-wrapper">
            <map-component :geoJson="geoJson" :inactiveMap="!tabActive || !transitionEnd" type="search" />
        </div>
        <div class="mobile-toggle-btn d-lg-none" @click="showList = !showList">
          <div v-if="showList">
            <i class="fas fa-map"></i> Visa karta
          </div>
          <div v-else>
            <i class="fas fa-list"></i> Visa lista
          </div>
        </div>
        <mobile-results-list
            :is-searching="isSearching"
            :search-query="searchQuery"
            :is-search="isSearch"
            :locations-list="locationsList"
            :walks-list="walksList"
            :handle-focus-location="handleFocusLocation"
            :handle-hover-location="handleHoverLocation"
            :showList="showList"
        >
        </mobile-results-list>
    </div>
</template>

<script>
/* global Bus, axios */
import MapComponent from '../map-component/MapComponent'
import { bbox, feature, point, distance, toWgs84 } from '@turf/turf'
import { setupGeoJson } from '../../helpers'
import { transformExtent } from 'ol/proj'
import { mapState } from 'vuex'
import MobileResultsList from './MobileResultsList'

export default {
  components: { MobileResultsList, MapComponent },
  props: ['searchQuery', 'searchResults', 'tabActive'],

  data: function () {
    return {
      focusedLocation: null,
      showResultsList: true,
      transitionEnd: true,
      isSearching: false,
      visibleFeatures: null,
      showList: false
    }
  },

  mounted () {
    this.$refs.resultsList.addEventListener('transitionend', () => {
      this.transitionEnd = true
    })

    Bus.$on('isSearching', searching => {
      this.isSearching = searching
    })

    Bus.$on('visibleFeatures', features => {
      this.handleVisibleFeatures(features)
    })
  },

  methods: {
    handleVastmanlandClick: function () {
      Bus.$emit('zoomInVastmanland')
    },

    handleVisibleFeatures: function (features) {
      const featureIds = _.map(features, feat => feat.get('id'))
      const result = _.get(this.searchResults.locations, 'result')
      this.visibleFeatures = _(result).filter(location => _.includes(featureIds, location.id)).value()
    },

    handleToggleList: function () {
      this.transitionEnd = false
      this.showResultsList = !this.showResultsList
    },

    handleFocusLocation: function (location) {
      const coordinates = [location.longitude, location.latitude]
      const extent = bbox(feature({
        type: 'Point',
        coordinates
      }))
      const mExtent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857')
      Bus.$emit('focusLocation', mExtent)
    },

    handleHoverLocation: function (location, out) {
      const coordinates = [location.longitude, location.latitude]
      const extent = bbox(feature({
        type: 'Point',
        coordinates
      }))
      const mExtent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857')
      Bus.$emit('hoverLocation', mExtent, out)
    },

    getDistanceString: function (value) {
      return value < 1000
        ? `${value}m`
        : `${(value / 1000).toFixed(2)}km`
    }
  },

  computed: {
    isSearch: function () {
      return !_.isEmpty(this.searchQuery) || !_(this.filters).flatMap().isEmpty()
    },
    geoJson: function () {
      return setupGeoJson(
        _.get(this.searchResults, 'locations.result'),
        this.searchResults.addressLocations,
        _.get(this.searchResults, 'walks.result', [])
      )
    },
    walksList: function () {
      return _.get(this.searchResults, 'walks.result', [])
    },
    numWalks: function () {
      return _.get(this.searchResults, 'walks.count', 0)
    },

    hasSearchResults: function () {
      return (_.get(this.searchResults, 'matches') && _.get(this.searchResults, 'matches') > 0 && this.isSearch)
    },

    locationsList: function () {
      if (this.isSearch) {
        if (this.geoLocation) {
          const geoPoint = toWgs84(point(this.geoLocation))
          return _(_.get(this.searchResults.locations, 'result')).map(location => {
            const locPoint = point([location.longitude, location.latitude])
            return { ...location, distance: parseInt(distance(geoPoint, locPoint, { units: 'meters' })) }
          }).orderBy('distance').value()
        } else {
          return _.get(this.searchResults.locations, 'result')
        }
      } else {
        if (this.geoLocation) {
          const geoPoint = toWgs84(point(this.geoLocation))
          return _(this.visibleFeatures).map(location => {
            const locPoint = point([location.longitude, location.latitude])
            return { ...location, distance: parseInt(distance(geoPoint, locPoint, { units: 'meters' })) }
          }).orderBy('distance').value()
        } else {
          return this.visibleFeatures
        }
      }
    },

    ...mapState({
      geoLocation: state => state.map.geoLocation,
      filters: state => state.search.filtered
    })
  }
}
</script>
